// Pan-India standalone topic catalog — drives non-city-prefixed SEO landing pages.
// Each entry generates a unique landing page at /:slug targeting pan-India intent
// (e.g. parents who search "maths tutors" without a city qualifier).
//
// Schema for each topic:
//   slug        — URL path segment
//   label       — human-readable title (used in H1 + nav)
//   h1          — page H1 (with {tutors} placeholder if needed)
//   keywords    — comma-separated string for meta keywords
//   kind        — service | subject | board | exam | grade | mode
//   intentMod   — "best", "top", "near-me", "female", etc. for content nuance
//   subjectRef  — references SUBJECTS slug (when kind === subject)
//   boardRef    — references BOARDS slug (when kind === board)

import { SUBJECTS, BOARDS, EXAMS, TRUST } from "./seoData";

const SUBJECT_TUTOR = (slug, label) => ({
  slug: `${slug}-tutors`,
  label: `${label} Tutors`,
  h1: `${label} Tutors — 1:1 Home & Online Tuition Across India`,
  kind: "subject",
  subjectRef: slug,
});

const BOARD_TUTOR = (slug, label) => ({
  slug: `${slug}-tutors`,
  label: `${label} Tutors`,
  h1: `${label} Tutors — Verified 1:1 Home & Online Tuition`,
  kind: "board",
  boardRef: slug,
});

const EXAM_TUTOR = (slug, label) => ({
  slug: `${slug}-tutors`,
  label: `${label} Tutors`,
  h1: `${label} Tutors — Personal Mentor-Driven Coaching`,
  kind: "exam",
  examRef: slug,
});

// High-intent service variants (no city)
const SERVICE_TOPICS = [
  { slug: "home-tutors-near-me", label: "Home Tutors Near Me", h1: "Home Tutors Near Me — Verified 1:1 Private Tutors", kind: "service", intentMod: "near-me" },
  { slug: "best-home-tutors", label: "Best Home Tutors", h1: "Best Home Tutors in India — 1:1 Verified, Reviewed & Rated", kind: "service", intentMod: "best" },
  { slug: "top-home-tutors", label: "Top Home Tutors", h1: "Top Home Tutors — Hand-Picked, ID-Verified & Demo-Cleared", kind: "service", intentMod: "top" },
  { slug: "private-tutors", label: "Private Tutors", h1: "Private Tutors — 1:1 Home & Online Tuition Across India", kind: "service", intentMod: "private" },
  { slug: "personal-tutor", label: "Personal Tutor", h1: "Personal Tutor — Dedicated 1:1 Teaching for Every Child", kind: "service", intentMod: "personal" },
  { slug: "one-to-one-tutor", label: "One-to-One Tutor", h1: "One-to-One Tutor — Truly Personalised Learning", kind: "service", intentMod: "one-to-one" },
  { slug: "home-teachers", label: "Home Teachers", h1: "Home Teachers — Qualified Educators at Your Doorstep", kind: "service", intentMod: "teacher" },
  { slug: "female-home-tutors", label: "Female Home Tutors", h1: "Female Home Tutors — Safe, Verified, Background-Checked", kind: "service", intentMod: "female" },
  { slug: "male-home-tutors", label: "Male Home Tutors", h1: "Male Home Tutors — Experienced 1:1 Educators", kind: "service", intentMod: "male" },
  { slug: "experienced-home-tutors", label: "Experienced Home Tutors", h1: "Experienced Home Tutors — 5+ Years of Teaching Excellence", kind: "service", intentMod: "experienced" },
  { slug: "verified-home-tutors", label: "Verified Home Tutors", h1: "Verified Home Tutors — 100% ID, Qualification & Demo Checked", kind: "service", intentMod: "verified" },
  { slug: "premium-home-tutors", label: "Premium Home Tutors", h1: "Premium Home Tutors — Elite Educators for Discerning Parents", kind: "service", intentMod: "premium" },
  { slug: "affordable-home-tutors", label: "Affordable Home Tutors", h1: "Affordable Home Tutors — Quality 1:1 Teaching Within Budget", kind: "service", intentMod: "affordable" },
  { slug: "online-tutors", label: "Online Tutors", h1: "Online Tutors — 1:1 Live Classes for Every Subject & Board", kind: "service", intentMod: "online" },
  { slug: "1-on-1-online-tutors", label: "1-on-1 Online Tutors", h1: "1-on-1 Online Tutors — Live, Interactive, Result-Oriented", kind: "service", intentMod: "1-on-1-online" },
];

// Subject tutors — pan-India landing pages
const SUBJECT_HOT_SLUGS = [
  ["mathematics", "Maths"],
  ["physics", "Physics"],
  ["chemistry", "Chemistry"],
  ["biology", "Biology"],
  ["english", "English"],
  ["hindi", "Hindi"],
  ["accounts", "Accounts"],
  ["economics", "Economics"],
  ["computer-science", "Computer Science"],
  ["coding", "Coding"],
  ["spoken-english", "Spoken English"],
];

// Board tutors — pan-India
const BOARD_HOT_SLUGS = [
  ["cbse", "CBSE"],
  ["icse", "ICSE"],
  ["ib", "IB"],
  ["igcse", "IGCSE"],
  ["a-level", "A-Level"],
  ["ap-curriculum", "AP"],
  ["nios", "NIOS"],
];

// Exam tutors — pan-India
const EXAM_HOT_SLUGS = [
  ["neet", "NEET"],
  ["jee", "JEE"],
  ["cuet", "CUET"],
];

export const STANDALONE_TOPICS = [
  ...SERVICE_TOPICS,
  ...SUBJECT_HOT_SLUGS.map(([slug, label]) => SUBJECT_TUTOR(slug, label)),
  ...BOARD_HOT_SLUGS.map(([slug, label]) => BOARD_TUTOR(slug, label)),
  ...EXAM_HOT_SLUGS.map(([slug, label]) => EXAM_TUTOR(slug, label)),
];

export const STANDALONE_TOPIC_MAP = Object.fromEntries(
  STANDALONE_TOPICS.map((t) => [t.slug, t])
);

export const STANDALONE_SLUGS = STANDALONE_TOPICS.map((t) => t.slug);

// ---------- Copy builders ----------
export function buildStandaloneTopicCopy(topic) {
  const v = {
    label: topic.label,
    rating: TRUST.ratingValue,
    reviews: TRUST.reviewCount,
    tutors: TRUST.tutorCount,
    students: TRUST.studentCount,
    years: TRUST.yearsExperience,
  };

  const subj = topic.kind === "subject" ? SUBJECTS.find((s) => s.slug === topic.subjectRef) : null;
  const brd = topic.kind === "board" ? BOARDS.find((b) => b.slug === topic.boardRef) : null;
  const exm = topic.kind === "exam" ? EXAMS.find((e) => e.slug === topic.examRef) : null;

  // Title + meta
  const title = `${topic.label} — ${v.tutors} Verified 1:1 Tutors | Unique Home Tutors`;
  let metaDescription = "";
  if (subj) {
    metaDescription = `${subj.name} tutors for 1:1 home & online tuition across India. Nursery to Class 12, all boards (CBSE, ICSE, IB, IGCSE). ${v.rating}/5 rating. Free demo. Call +91 7000530316.`;
  } else if (brd) {
    metaDescription = `${brd.name} tutors for 1:1 home & online tuition. Aligned with the latest ${brd.full} syllabus. Class 1 to 12, all subjects. ${v.rating}/5 from ${v.reviews}+ parents. Free demo.`;
  } else if (exm) {
    metaDescription = `${exm.name} (${exm.full}) preparation with dedicated mentor-driven 1:1 coaching. Weekly mocks, doubt clearing 6 days a week, parent updates every 2 weeks. Free demo class.`;
  } else if (topic.intentMod === "near-me") {
    metaDescription = `Find home tutors near you — verified 1:1 private tutors in Jabalpur, Nagpur, and pan-India online. Nursery to Class 12, all boards & subjects. Book a free demo today.`;
  } else if (topic.intentMod === "female") {
    metaDescription = `Verified female home tutors — safe, qualified, ID-checked. 1:1 home and online tuition for Nursery to Class 12, all boards. Free demo. Call +91 7000530316.`;
  } else if (topic.intentMod === "online") {
    metaDescription = `1:1 live online tutors for CBSE, ICSE, IB, IGCSE, NEET, JEE and all subjects. Pan-India and worldwide. Recorded sessions, timezone-friendly slots. Free demo.`;
  } else if (topic.intentMod === "best" || topic.intentMod === "top") {
    metaDescription = `${topic.label} — ID-verified, demo-cleared educators. 1:1 home and online tuition for all classes, boards & subjects. ${v.rating}/5 rating from ${v.reviews}+ parents.`;
  } else {
    metaDescription = `${topic.label} for 1:1 home and online tuition — Nursery to Class 12, every board, every subject. ${v.tutors} verified educators. Free demo. Call +91 7000530316.`;
  }
  metaDescription = metaDescription.slice(0, 158);

  const keywords = [
    topic.label.toLowerCase(),
    `${topic.label.toLowerCase()} india`,
    `${topic.label.toLowerCase()} near me`,
    subj ? `${subj.name.toLowerCase()} tutor` : null,
    brd ? `${brd.name.toLowerCase()} tutor` : null,
    exm ? `${exm.name.toLowerCase()} coaching` : null,
    "1:1 home tuition",
    "online tuition",
    "private tutor",
    "verified home tutors",
  ].filter(Boolean).join(", ");

  // Intro
  let intro;
  if (subj) {
    intro = `Looking for expert ${subj.name} tutors? Unique Home Tutors connects students with ${v.tutors}+ verified 1:1 educators specialising in ${subj.name} — from foundation (Class 1) through Class 12 board prep, NEET/JEE alignment and college-level support. Choose home tuition in Jabalpur or Nagpur, or 1:1 live online from anywhere in India and worldwide.`;
  } else if (brd) {
    intro = `Get top-rated ${brd.name} tutors aligned with the latest ${brd.full} curriculum. We cover every class, every subject and every paper pattern. Whether you need a 1:1 home tutor (in Jabalpur or Nagpur) or 1:1 live online sessions Pan-India, we match you with a mentor whose teaching style fits your child.`;
  } else if (exm) {
    intro = `Crack ${exm.name} (${exm.full}) with a dedicated personal mentor — not a 200-student mega-batch. Our ${exm.name} tutors deliver custom 2-year integrated programs, weekly mocks, daily doubt-clearing and bi-weekly parent reports. Available as 1:1 home tuition in Jabalpur/Nagpur or 1:1 live online anywhere.`;
  } else if (topic.intentMod === "female") {
    intro = `Parents looking for verified female home tutors — every tutor on our network passes ID verification, qualification proof, a demo evaluation and continuous feedback monitoring. Safe, professional, and dedicated 1:1 attention for Nursery to Class 12 across all boards.`;
  } else if (topic.intentMod === "online") {
    intro = `1:1 live online tutors deliver the same quality as in-person tuition without the commute. Sessions on interactive whiteboard + screen-share + chat, recorded on request, and timezone-matched for Indian and NRI families. ${v.tutors}+ verified educators, all subjects, all boards.`;
  } else if (topic.intentMod === "near-me") {
    intro = `Searching for home tutors near you? Unique Home Tutors has verified 1:1 educators in Jabalpur, Nagpur and Pan-India online — covering Nursery to Class 12, all boards (CBSE, ICSE, IB, IGCSE) and competitive prep (NEET, JEE, CUET, Olympiad). Free demo before any commitment.`;
  } else if (topic.intentMod === "best" || topic.intentMod === "top") {
    intro = `Unique Home Tutors is rated ${v.rating}/5 by ${v.reviews}+ parents — built on ${v.years} years of teaching excellence and ${v.tutors}+ verified educators. We don't just send "any tutor" — we match the right mentor to your child's grade, board, learning pace and personality.`;
  } else {
    intro = `Unique Home Tutors is India's most trusted 1:1 tuition brand — ${v.years} years strong, ${v.tutors}+ verified educators, and a ${v.rating}/5 parent rating. Whether you need a home tutor in Jabalpur or Nagpur, or 1:1 live online classes Pan-India and worldwide, we have the right mentor for your child.`;
  }

  // Bullets
  const bullets = [
    "100% ID-verified, qualification-checked tutors",
    "1:1 attention — never a batch, never shared",
    "All boards: CBSE, ICSE, IB, IGCSE, A-Level, AP, State Boards",
    "All grades: Nursery to Class 12 + Droppers + Foundation",
    "Free demo class before any commitment",
    "Refund available on unused sessions (12/20-session plans)",
  ];

  // Sections
  const sections = [
    {
      h2: subj ? `Why dedicated ${subj.name} tutors win` : brd ? `Why dedicated ${brd.name} tutors win` : `Why parents choose us`,
      body: subj
        ? `${subj.name} requires a teacher who can break complex concepts into simple, intuitive steps — and reinforce them with the right kind of practice. Our ${subj.name} mentors are subject specialists (not generalists), with proven track records across CBSE, ICSE, IB and IGCSE.`
        : brd
        ? `${brd.name} curriculum has its own pace, assessment style and content depth. Our ${brd.name} tutors are alumni of the same board (or have taught it for 5+ years), so they know exactly which topics need deep coverage and which need rapid revision.`
        : `Three things set us apart: (1) tutor verification quality — every educator passes ID, qualification, demo and continuous monitoring; (2) refund transparency — pay-as-you-go monthly cycle, refunds on unused sessions; (3) a real human student-success manager (not just a dashboard) to track progress every fortnight.`,
    },
    {
      h2: "Modes of teaching — choose what fits",
      body: "Three options: (a) 1:1 home tuition in Jabalpur or Nagpur — the tutor travels to your home; (b) small-batch coaching at our Jabalpur (Ghadi Chowk) or Nagpur (Dharampeth) centres; (c) 1:1 live online classes anywhere in India and worldwide for NRI families. Recordings shared on request, timezone-friendly slots, transparent fees.",
    },
    {
      h2: "How the matching works",
      body: "Tell us the grade, board, subject (or exam), preferred mode and any specific needs (female tutor, evening slot, accent training, etc.). We share 2-3 matched tutor profiles, you pick one for a free demo class, and we onboard from there. Tutor changes are free if the fit isn't right.",
    },
    {
      h2: "Fees & refunds",
      body: "Monthly cycle — pay advance, classes resume; delayed fees pause classes. We offer 12-session and 20-session plans with refunds on unused sessions. No hidden charges. No signing fees. No long-term lock-in.",
    },
  ];

  // FAQs
  const faqs = [
    {
      q: subj ? `Do you have ${subj.name} tutors for all classes?` : brd ? `Do your ${brd.name} tutors cover all subjects?` : "Are all tutors verified?",
      a: subj
        ? `Yes — ${subj.name} tutors covering Nursery to Class 12, all boards including CBSE, ICSE, IB, IGCSE and State Boards, plus NEET/JEE level for senior classes.`
        : brd
        ? `Yes — ${brd.name} tutors for every subject (Mathematics, Sciences, Languages, Humanities, Commerce, Computer Science) and every grade level offered by the board.`
        : `Yes. Every tutor passes ID verification, qualification proof, a demo class evaluation and continuous parent-feedback monitoring. Tutors who slip on quality are removed from our network.`,
    },
    {
      q: "Is there a free demo class?",
      a: "Yes — every new enquiry gets one free demo before any payment. You evaluate the tutor's teaching style and child's comfort, then decide.",
    },
    {
      q: "What boards do you support?",
      a: "CBSE, ICSE, ISC, MP Board, Maharashtra Board, NIOS, IB (PYP/MYP/DP), IGCSE, GCSE, A-Level, AP Curriculum, US Common Core, Australian, and Canadian curricula.",
    },
    {
      q: "Can I get a tutor outside Jabalpur or Nagpur?",
      a: "Yes — for cities outside Jabalpur and Nagpur, we offer 1:1 live online tuition Pan-India and worldwide. Same verified-tutor quality, timezone-matched slots.",
    },
    {
      q: "How quickly can a tutor start?",
      a: "Most demos happen within 24-48 hours of enquiry. Call +91 7000530316 to expedite or use the WhatsApp form on this page.",
    },
  ];

  return { title, metaDescription, keywords, intro, bullets, sections, faqs };
}
